import { getTranslatedErrorMessage } from "./errorCodeMapper.js";
import { untranslated, type TranslatedString } from "./types/i18n.js";

/**
 * The failure half of any backend response, as it reaches a component or a screen.
 *
 * Discriminated on `status`: a refusal MUST name an `errorCode`, which is what lets the resolver
 * below have no "no code" branch. The server never puts user-facing English on the wire — the code
 * (plus `params` for a `{{placeholder}}` sentence) is the whole contract.
 */
export type ApiErrorBody =
  | {
      status: false;
      errorCode: number;
      params?: Record<string, string | number> | undefined;
    }
  /** The success arm, so a status-discriminated response body assigns without narrowing first. */
  | { status?: true | undefined; errorCode?: undefined; params?: undefined };

/**
 * The `t` each app injects. Plain `string` in and out because this package must never depend on
 * i18next — it is compiled into the Node server too.
 */
export type ErrorContentTranslate = (key: string, options?: Record<string, string | number>) => string;

/**
 * Turns a failure body into the text to show the user — the single implementation behind both
 * `packages/web/src/i18n/errorContent.ts` and `packages/mobile/src/i18n/errorContent.ts`.
 *
 * There is no caller-supplied fallback: the code always resolves to a sentence, and
 * `getTranslatedErrorMessage` already answers "Something went wrong" for a code with no entry.
 * `untranslated` is the brand cast, not a translation bypass — the string has been through `t()`.
 */
export function resolveErrorContentWith(
  t: ErrorContentTranslate,
  body: ApiErrorBody | null | undefined,
): TranslatedString {
  if (body?.status === false) {
    return untranslated(getTranslatedErrorMessage(body.errorCode, body.params, t));
  }
  // Reached only with no body at all — a rejection that never carried one.
  return untranslated(t("Something went wrong"));
}

/**
 * {@link resolveErrorContentWith} for an axios rejection, where the body sits under
 * `error.response.data` and is `unknown` until probed.
 */
export function resolveThrownErrorContentWith(t: ErrorContentTranslate, error: unknown): TranslatedString {
  const data: unknown = (error as { response?: { data?: unknown } } | null)?.response?.data;
  const body = typeof data === "object" && data !== null ? (data as ApiErrorBody) : undefined;
  return resolveErrorContentWith(t, body);
}
